import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../config/supabase';
import innatesMiddlewareService from '../services/innatesMiddlewareService';
import { 
  MapPinIcon,
  HomeIcon,
  GlobeAltIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';
import { GoogleMap, useJsApiLoader, Marker } from '@react-google-maps/api';

const GOOGLE_MAPS_API_KEY = 'AIzaSyAxQc3AOPO0jYdzGokZl6lV7LnqaaUi3Mw';

// Smart Farm Organisation ID - all users registered via the dashboard are assigned to this organisation
const SMARTFARM_ORGANISATION_ID = 4345;

// Google Maps libraries to load
const libraries = ['places'];

// Map container style
const mapContainerStyle = {
  width: '100%',
  height: '300px',
  borderRadius: '8px'
};

// Default center (Kuala Lumpur)
const defaultCenter = {
  lat: 3.1390,
  lng: 101.6869
};

// Map options
const mapOptions = {
  disableDefaultUI: false,
  zoomControl: true,
  mapTypeControl: true,
  streetViewControl: false,
  fullscreenControl: true,
  mapTypeId: 'hybrid'
};

const SetupFarm = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [formData, setFormData] = useState({
    farmName: '',
    farmSize: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    postcode: '',
    latitude: '',
    longitude: ''
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [markerPosition, setMarkerPosition] = useState(null);
  const mapRef = useRef(null);

  // Load Google Maps API
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries
  });

  // Store map reference
  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
  }, []);

  // Handle marker drag end
  const onMarkerDragEnd = useCallback((e) => {
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();
    
    setMarkerPosition({ lat, lng });
    setFormData(prev => ({
      ...prev,
      latitude: lat.toFixed(6),
      longitude: lng.toFixed(6)
    }));
    
    // Reverse geocode to get address
    reverseGeocode(lat, lng);
  }, []);

  // Handle map click to place marker
  const onMapClick = useCallback((e) => {
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();
    
    setMarkerPosition({ lat, lng });
    setFormData(prev => ({
      ...prev,
      latitude: lat.toFixed(6),
      longitude: lng.toFixed(6)
    }));
    
    // Reverse geocode to get address
    reverseGeocode(lat, lng);
  }, []);
  
  const [states] = useState([
    'Johor', 'Kedah', 'Kelantan', 'Melaka', 'Negeri Sembilan',
    'Pahang', 'Perak', 'Perlis', 'Pulau Pinang', 'Sabah',
    'Sarawak', 'Selangor', 'Terengganu', 'Kuala Lumpur', 'Labuan', 'Putrajaya'
  ]);

  // Malaysia state mapping for Google Places
  const stateMapping = {
    'johor': 'Johor',
    'kedah': 'Kedah',
    'kelantan': 'Kelantan',
    'melaka': 'Melaka',
    'malacca': 'Melaka',
    'negeri sembilan': 'Negeri Sembilan',
    'pahang': 'Pahang',
    'perak': 'Perak',
    'perlis': 'Perlis',
    'pulau pinang': 'Pulau Pinang',
    'penang': 'Pulau Pinang',
    'sabah': 'Sabah',
    'sarawak': 'Sarawak',
    'selangor': 'Selangor',
    'terengganu': 'Terengganu',
    'kuala lumpur': 'Kuala Lumpur',
    'wilayah persekutuan kuala lumpur': 'Kuala Lumpur',
    'labuan': 'Labuan',
    'putrajaya': 'Putrajaya',
    'wilayah persekutuan putrajaya': 'Putrajaya'
  };

  // Get Google Maps link for opening in new tab
  const getGoogleMapsLink = () => {
    const lat = formData.latitude || '3.1390';
    const lng = formData.longitude || '101.6869';
    return `https://www.google.com/maps?q=${lat},${lng}`;
  };

  // Geocode address using Google Geocoding API
  const geocodeAddress = async (address) => {
    if (!address.trim()) return;
    
    setIsSearching(true);
    try {
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(address)}&components=country:MY&key=${GOOGLE_MAPS_API_KEY}`
      );
      const data = await response.json();
      
      if (data.status === 'OK' && data.results && data.results[0]) {
        const result = data.results[0];
        const location = result.geometry.location;
        
        // Update coordinates
        setFormData(prev => ({
          ...prev,
          latitude: location.lat.toFixed(6),
          longitude: location.lng.toFixed(6)
        }));
        
        // Update marker position
        setMarkerPosition({ lat: location.lat, lng: location.lng });
        
        // Pan map to new location
        if (mapRef.current) {
          mapRef.current.panTo({ lat: location.lat, lng: location.lng });
          mapRef.current.setZoom(17);
        }
        
        // Parse address components
        parseAddressComponents(result.address_components, result.formatted_address);
      } else {
        console.log('Geocoding failed:', data.status);
      }
    } catch (error) {
      console.error('Geocoding error:', error);
    } finally {
      setIsSearching(false);
    }
  };

  // Reverse geocode coordinates
  const reverseGeocode = async (lat, lng) => {
    try {
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${GOOGLE_MAPS_API_KEY}`
      );
      const data = await response.json();
      
      if (data.status === 'OK' && data.results && data.results[0]) {
        parseAddressComponents(data.results[0].address_components, data.results[0].formatted_address);
      }
    } catch (error) {
      console.error('Reverse geocoding error:', error);
    }
  };

  // Parse Google address components into form fields
  const parseAddressComponents = (components, formattedAddress) => {
    if (!components) return;

    let streetNumber = '';
    let route = '';
    let locality = '';
    let adminArea = '';
    let postalCode = '';

    components.forEach(component => {
      const types = component.types;
      if (types.includes('street_number')) {
        streetNumber = component.long_name;
      }
      if (types.includes('route')) {
        route = component.long_name;
      }
      if (types.includes('locality') || types.includes('sublocality_level_1')) {
        locality = component.long_name;
      }
      if (types.includes('administrative_area_level_1')) {
        adminArea = component.long_name;
      }
      if (types.includes('postal_code')) {
        postalCode = component.long_name;
      }
    });

    // Build address line
    let addressLine1 = '';
    if (streetNumber && route) {
      addressLine1 = `${streetNumber} ${route}`;
    } else if (route) {
      addressLine1 = route;
    } else if (formattedAddress) {
      const parts = formattedAddress.split(',');
      addressLine1 = parts[0].trim();
    }

    // Map state name
    const mappedState = stateMapping[adminArea.toLowerCase()] || '';

    setFormData(prev => ({
      ...prev,
      addressLine1: addressLine1 || prev.addressLine1,
      city: locality || prev.city,
      state: mappedState || prev.state,
      postcode: postalCode || prev.postcode
    }));

    if (addressLine1) {
      setErrors(prev => ({ ...prev, addressLine1: '' }));
    }
  };

  // Get current location on mount
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;
          setFormData(prev => ({
            ...prev,
            latitude: lat.toFixed(6),
            longitude: lng.toFixed(6)
          }));
          // Set marker position
          setMarkerPosition({ lat, lng });
          // Reverse geocode to get address
          reverseGeocode(lat, lng);
        },
        (error) => {
          console.log('Geolocation error:', error);
        }
      );
    }
  }, []);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.farmName.trim()) {
      newErrors.farmName = 'Farm name is required';
    }
    
    if (!formData.farmSize || parseFloat(formData.farmSize) <= 0) {
      newErrors.farmSize = 'Please enter a valid farm size';
    }
    
    if (!formData.addressLine1.trim()) {
      newErrors.addressLine1 = 'Address is required';
    }
    
    if (!formData.city.trim()) {
      newErrors.city = 'City is required';
    }
    
    if (!formData.state) {
      newErrors.state = 'State is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsLoading(true);
    
    try {
      // 1. Create address record
      const { data: addressData, error: addressError } = await supabase
        .from('address')
        .insert({
          addressLine1: formData.addressLine1,
          addressLine2: formData.addressLine2,
          city: formData.city,
          state: formData.state,
          postcode: formData.postcode,
          latitude: formData.latitude ? parseFloat(formData.latitude) : null,
          longitude: formData.longitude ? parseFloat(formData.longitude) : null,
          addressFull: `${formData.addressLine1}, ${formData.city}, ${formData.state} ${formData.postcode}`,
          created_at: new Date().toISOString()
        })
        .select()
        .single();
      
      if (addressError) throw addressError;
      console.log('✅ Address created:', addressData.id);

      // 2. Get user's custom ID from user table
      const { data: userData, error: userFetchError } = await supabase
        .from('user')
        .select('id')
        .eq('id_auth', user.id)
        .single();
      
      if (userFetchError) {
        console.error('Error fetching user:', userFetchError);
      }

      // 3. Create organisation record
      const { data: orgData, error: orgError } = await supabase
        .from('organisation')
        .insert({
          name_org: `${formData.farmName} Organisation`,
          company: false,
          created_by: userData?.id,
          created_at: new Date().toISOString()
        })
        .select()
        .single();
      
      if (orgError) {
        console.error('Error creating organisation:', orgError);
      }
      console.log('✅ Organisation created:', orgData?.id);

      // 4. Create farm record
      const { data: farmData, error: farmError } = await supabase
        .from('farm')
        .insert({
          farmName: formData.farmName,
          farmsize: parseFloat(formData.farmSize),
          address: addressData.id,
          organisation: orgData?.id,
          currency: 1, // Default currency (MYR)
          created_at: new Date().toISOString()
        })
        .select()
        .single();
      
      if (farmError) throw farmError;
      console.log('✅ Farm created:', farmData.id);

      // 4b. Register farm with Innates middleware (non-blocking)
      try {
        const location = formData.latitude && formData.longitude 
          ? `${formData.latitude},${formData.longitude}` 
          : '';
        const middlewareResponse = await innatesMiddlewareService.registerFarm({
          farmerId: String(userData?.id || ''),
          farmId: String(farmData.id),
          location: location
        });
        console.log('✅ Farm registered with Innates middleware:', middlewareResponse);
        
        // Store middleware_id for future sync operations
        if (middlewareResponse?.id) {
          await supabase
            .from('farm')
            .update({ middleware_id: middlewareResponse.id })
            .eq('id', farmData.id);
          console.log('✅ Farm middleware ID stored:', middlewareResponse.id);
        }
      } catch (middlewareError) {
        // Log but don't block farm creation if middleware fails
        console.warn('⚠️ Innates middleware farm registration failed (non-blocking):', middlewareError.message);
      }

      // 5. Create farm_user_role record (assign user as owner)
      if (userData?.id) {
        const { data: furData, error: furError } = await supabase
          .from('farm_user_role')
          .insert({
            farm: farmData.id,
            user: userData.id,
            role: 2, // Owner role (matching Kapitani app)
            created_at: new Date().toISOString()
          })
          .select()
          .single();
        
        if (furError) {
          console.error('Error creating farm_user_role:', furError);
        } else {
          console.log('✅ Farm user role created:', furData.id);
          
          // 6. Update user's default farm
          await supabase
            .from('user')
            .update({ 
              farm_default: farmData.id,
              default_fur: furData.id
            })
            .eq('id', userData.id);
          
          // 7. Create user_org entry linking user to Smart Farm organisation
          const { data: userOrgData, error: userOrgError } = await supabase
            .from('user_org')
            .insert({
              user: userData.id,
              organisation: SMARTFARM_ORGANISATION_ID,
              created_at: new Date().toISOString()
            })
            .select()
            .single();
          
          if (userOrgError) {
            console.error('Error creating user_org:', userOrgError);
          } else {
            console.log('✅ User linked to Smart Farm organisation:', userOrgData.id);
            
            // 8. Create user_org_farm entry linking user_org to their farm
            const { data: userOrgFarmData, error: userOrgFarmError } = await supabase
              .from('user_org_farm')
              .insert({
                user_org: userOrgData.id,
                farm: farmData.id,
                created_at: new Date().toISOString()
              })
              .select()
              .single();
            
            if (userOrgFarmError) {
              console.error('Error creating user_org_farm:', userOrgFarmError);
            } else {
              console.log('✅ User-org-farm link created:', userOrgFarmData.id);
            }
          }
        }
      }

      // Navigate to cycle setup
      navigate('/setup/cycle', { 
        state: { 
          farmId: farmData.id,
          farmName: formData.farmName,
          userId: userData?.id
        }
      });
      
    } catch (error) {
      console.error('Farm setup error:', error);
      setErrors({ submit: error.message || 'Failed to create farm. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-lg w-full space-y-8">
        {/* Logo and Header */}
        <div className="text-center">
          <div className="mx-auto h-16 w-16 flex items-center justify-center mb-4">
            <img src="/logo-Kapitani.png" alt="Kapitani Logo" className="h-16 w-16 object-contain" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Set Up Your Farm
          </h2>
          <p className="text-gray-600">
            Tell us about your farm
          </p>
          
          {/* Progress Steps */}
          <div className="flex items-center justify-center mt-6 space-x-2">
            <div className="flex items-center">
              <div className="w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center text-sm font-semibold">✓</div>
              <span className="ml-2 text-sm font-medium text-green-600">Account</span>
            </div>
            <div className="w-8 h-0.5 bg-green-500"></div>
            <div className="flex items-center">
              <div className="w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center text-sm font-semibold">2</div>
              <span className="ml-2 text-sm font-medium text-green-600">Farm</span>
            </div>
            <div className="w-8 h-0.5 bg-gray-300"></div>
            <div className="flex items-center">
              <div className="w-8 h-8 bg-gray-300 text-gray-500 rounded-full flex items-center justify-center text-sm font-semibold">3</div>
              <span className="ml-2 text-sm text-gray-500">Cycle</span>
            </div>
          </div>
        </div>

        {/* Farm Setup Form */}
        <div className="bg-white rounded-lg shadow-lg p-8">
          <form className="space-y-5" onSubmit={handleSubmit}>
            {errors.submit && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {errors.submit}
              </div>
            )}

            {/* Farm Name */}
            <div>
              <label htmlFor="farmName" className="block text-sm font-medium text-gray-700 mb-1">
                Farm Name *
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <HomeIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="farmName"
                  name="farmName"
                  type="text"
                  value={formData.farmName}
                  onChange={handleInputChange}
                  className={`input pl-10 ${errors.farmName ? 'border-red-500' : ''}`}
                  placeholder="My Farm"
                />
              </div>
              {errors.farmName && <p className="mt-1 text-xs text-red-500">{errors.farmName}</p>}
            </div>

            {/* Farm Size */}
            <div>
              <label htmlFor="farmSize" className="block text-sm font-medium text-gray-700 mb-1">
                Farm Size (hectares) *
              </label>
              <input
                id="farmSize"
                name="farmSize"
                type="number"
                step="0.01"
                min="0"
                value={formData.farmSize}
                onChange={handleInputChange}
                className={`input ${errors.farmSize ? 'border-red-500' : ''}`}
                placeholder="e.g. 5.5"
              />
              {errors.farmSize && <p className="mt-1 text-xs text-red-500">{errors.farmSize}</p>}
            </div>

            {/* Address */}
            <div>
              <label htmlFor="addressLine1" className="block text-sm font-medium text-gray-700 mb-1">
                Address Line 1 *
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MapPinIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="addressLine1"
                  name="addressLine1"
                  type="text"
                  value={formData.addressLine1}
                  onChange={handleInputChange}
                  className={`input pl-10 ${errors.addressLine1 ? 'border-red-500' : ''}`}
                  placeholder="Street address"
                />
              </div>
              {errors.addressLine1 && <p className="mt-1 text-xs text-red-500">{errors.addressLine1}</p>}
            </div>

            <div>
              <label htmlFor="addressLine2" className="block text-sm font-medium text-gray-700 mb-1">
                Address Line 2
              </label>
              <input
                id="addressLine2"
                name="addressLine2"
                type="text"
                value={formData.addressLine2}
                onChange={handleInputChange}
                className="input"
                placeholder="Apt, suite, unit, etc. (optional)"
              />
            </div>

            {/* City, State, Postcode Row */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1">
                  City *
                </label>
                <input
                  id="city"
                  name="city"
                  type="text"
                  value={formData.city}
                  onChange={handleInputChange}
                  className={`input ${errors.city ? 'border-red-500' : ''}`}
                  placeholder="City"
                />
                {errors.city && <p className="mt-1 text-xs text-red-500">{errors.city}</p>}
              </div>
              <div>
                <label htmlFor="postcode" className="block text-sm font-medium text-gray-700 mb-1">
                  Postcode
                </label>
                <input
                  id="postcode"
                  name="postcode"
                  type="text"
                  value={formData.postcode}
                  onChange={handleInputChange}
                  className="input"
                  placeholder="12345"
                />
              </div>
            </div>

            <div>
              <label htmlFor="state" className="block text-sm font-medium text-gray-700 mb-1">
                State *
              </label>
              <select
                id="state"
                name="state"
                value={formData.state}
                onChange={handleInputChange}
                className={`input ${errors.state ? 'border-red-500' : ''}`}
              >
                <option value="">Select a state</option>
                {states.map(state => (
                  <option key={state} value={state}>{state}</option>
                ))}
              </select>
              {errors.state && <p className="mt-1 text-xs text-red-500">{errors.state}</p>}
            </div>

            {/* Location Picker Section */}
            <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
              <div className="flex items-center justify-between mb-3">
                <label className="block text-sm font-medium text-gray-700">
                  <MapPinIcon className="h-5 w-5 inline mr-1 text-green-600" />
                  Farm Location
                </label>
                <button
                  type="button"
                  onClick={() => setShowMap(!showMap)}
                  className="text-sm text-green-600 hover:text-green-700 font-medium flex items-center gap-1"
                >
                  {showMap ? 'Hide Map' : 'Pick on Map'}
                  <svg className={`w-4 h-4 transition-transform ${showMap ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              </div>

              {/* Map Container */}
              {showMap && (
                <div className="space-y-3">
                  {/* Search Box */}
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search address in Malaysia..."
                      className="input pl-10 pr-20 w-full"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          geocodeAddress(searchQuery);
                        }
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => geocodeAddress(searchQuery)}
                      disabled={isSearching || !searchQuery.trim()}
                      className="absolute right-2 top-1/2 -translate-y-1/2 px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSearching ? '...' : 'Search'}
                    </button>
                  </div>

                  {/* Interactive Google Map */}
                  <div className="w-full rounded-lg border border-gray-300 overflow-hidden relative">
                    {loadError && (
                      <div className="h-[300px] flex items-center justify-center bg-gray-100 text-red-500">
                        Error loading maps. Please check your API key.
                      </div>
                    )}
                    {!isLoaded && !loadError && (
                      <div className="h-[300px] flex items-center justify-center bg-gray-100">
                        <div className="flex items-center gap-2 text-gray-500">
                          <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Loading map...
                        </div>
                      </div>
                    )}
                    {isLoaded && !loadError && (
                      <GoogleMap
                        mapContainerStyle={mapContainerStyle}
                        center={markerPosition || defaultCenter}
                        zoom={markerPosition ? 17 : 12}
                        options={mapOptions}
                        onLoad={onMapLoad}
                        onClick={onMapClick}
                      >
                        {markerPosition && (
                          <Marker
                            position={markerPosition}
                            draggable={true}
                            onDragEnd={onMarkerDragEnd}
                            animation={window.google?.maps?.Animation?.DROP}
                          />
                        )}
                      </GoogleMap>
                    )}
                  </div>

                  <div className="flex items-center justify-between">
                    <p className="text-xs text-gray-500">
                      📍 Click on the map or drag the pin to set location
                    </p>
                    {formData.latitude && formData.longitude && (
                      <a
                        href={getGoogleMapsLink()}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-green-600 hover:text-green-700 flex items-center gap-1"
                      >
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                        Open in Google Maps
                      </a>
                    )}
                  </div>
                </div>
              )}

              {/* GPS Coordinates */}
              <div className="grid grid-cols-2 gap-4 mt-3">
                <div>
                  <label htmlFor="latitude" className="block text-xs font-medium text-gray-600 mb-1">
                    Latitude
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <GlobeAltIcon className="h-4 w-4 text-gray-400" />
                    </div>
                    <input
                      id="latitude"
                      name="latitude"
                      type="text"
                      value={formData.latitude}
                      onChange={handleInputChange}
                      className="input pl-9 text-sm"
                      placeholder="Auto-detected"
                      readOnly={showMap}
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="longitude" className="block text-xs font-medium text-gray-600 mb-1">
                    Longitude
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <GlobeAltIcon className="h-4 w-4 text-gray-400" />
                    </div>
                    <input
                      id="longitude"
                      name="longitude"
                      type="text"
                      value={formData.longitude}
                      onChange={handleInputChange}
                      className="input pl-9 text-sm"
                      placeholder="Auto-detected"
                      readOnly={showMap}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex justify-center items-center"
              >
                {isLoading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Creating Farm...
                  </>
                ) : (
                  'Continue to Cycle Setup →'
                )}
              </button>
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="text-center text-sm text-gray-500">
          <p>© 2026 Kapitani. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
};

export default SetupFarm;
